import {
  MAX_SCHEDULER_TARGETS,
  ScheduleTaskTypes,
} from '@risksmart-app/domain/src/types/consts';
import {
  MAX_INTERVAL,
  MAX_SEED_YEARS_AHEAD,
} from '@risksmart-app/scheduler-engine/src/index';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { StringDateSchema, UserOrGroupsSchema } from 'src/schemas/global';
import { z } from 'zod';

import {
  ENDS,
  findOverlap,
  resolveScheduleWindows,
  toCalendarDayUtc,
} from '@/utils/scheduler';
import { SCHEDULE_FREQUENCIES } from '@/utils/scheduler/frequencyKeys';

import { ASSIGNMENT_RULES } from './assignmentRule';
import { buildOccurrencePreview } from './occurrencePreview';

dayjs.extend(utc);

const requiredMessage = 'Required';

const ScheduleTargetSchema = z.object({
  value: z.string(),
  label: z.string(),
  /** Optional because a type with no id prefix contributes no row. */
  tags: z.array(z.string()).optional(),
  Type: z.nativeEnum(ScheduleTaskTypes),
});

const scheduleFormFields = z.object({
  Name: z.string().min(1, { message: requiredMessage }),
  Description: z.string().nullish(),
  /**
   * Stricter than the server, which defaults both owner arrays to empty: an
   * ownerless schedule has nobody to chase when its tasks go overdue.
   */
  Owners: UserOrGroupsSchema.min(1, {
    message: 'Select at least one schedule owner',
  }),
  /**
   * The empty string is the unselected state. A `nativeEnum` has no member for
   * "nothing chosen", and a nullable field would make every downstream read
   * defensive.
   */
  TaskType: z.union([z.nativeEnum(ScheduleTaskTypes), z.literal('')]),
  Targets: z
    .array(ScheduleTargetSchema)
    .min(1, { message: requiredMessage })
    .max(MAX_SCHEDULER_TARGETS, {
      message: `Select no more than ${MAX_SCHEDULER_TARGETS} objects`,
    }),
  Assignment: z.enum(ASSIGNMENT_RULES),
  FirstStartDate: StringDateSchema,
  FirstDueDate: StringDateSchema,
  Repeat: z.boolean(),
  Frequency: z.nativeEnum(SCHEDULE_FREQUENCIES),
  Interval: z.string(),
  Ends: z.enum(ENDS),
  Count: z.string(),
  Until: z.string(),
});

type ScheduleFormFields = z.infer<typeof scheduleFormFields>;

const isWholeNumberInRange = (
  value: string,
  min: number,
  max: number
): boolean => {
  if (!/^\d+$/.test(value)) {
    return false;
  }

  const parsed = Number(value);

  return Number.isSafeInteger(parsed) && parsed >= min && parsed <= max;
};

/**
 * These three rules restate the data-layer's create-schedule validator and can
 * drift from it. The symptom is a schedule the form accepts and the server
 * rejects.
 */
const validateTaskType = (
  values: ScheduleFormFields,
  ctx: z.RefinementCtx
): void => {
  // Required-ness lives here rather than as a `.refine` on the field: a refined
  // union narrows `''` out of the inferred type, and the unselected state has to
  // stay expressible for the default values.
  if (values.TaskType === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ['TaskType'],
    });
  }
};

const validateDates = (
  values: ScheduleFormFields,
  ctx: z.RefinementCtx
): void => {
  const startDate = toCalendarDayUtc(values.FirstStartDate);
  const dueDate = toCalendarDayUtc(values.FirstDueDate);
  if (startDate === null || dueDate === null) {
    return;
  }

  const today = dayjs.utc().startOf('day');

  if (dayjs.utc(startDate).isBefore(today)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'The start date cannot be in the past',
      path: ['FirstStartDate'],
    });
  }

  // Only an earlier due date is rejected; a same-day window is legal.
  if (dayjs.utc(dueDate).isBefore(startDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'The due date cannot be before the start date',
      path: ['FirstDueDate'],
    });
  }

  if (dayjs.utc(dueDate).isAfter(today.add(MAX_SEED_YEARS_AHEAD, 'year'))) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `The due date cannot be more than ${MAX_SEED_YEARS_AHEAD} years ahead`,
      path: ['FirstDueDate'],
    });
  }
};

const validateRecurrence = (
  values: ScheduleFormFields,
  ctx: z.RefinementCtx
): void => {
  if (!values.Repeat) {
    return;
  }

  if (!isWholeNumberInRange(values.Interval, 1, MAX_INTERVAL)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Enter a whole number between 1 and ${MAX_INTERVAL}`,
      path: ['Interval'],
    });
  }

  if (
    values.Ends === 'count' &&
    !isWholeNumberInRange(values.Count, 1, Number.MAX_SAFE_INTEGER)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Enter a whole number of occurrences',
      path: ['Count'],
    });
  }

  if (values.Ends === 'until') {
    if (toCalendarDayUtc(values.Until) === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ['Until'],
      });
    } else if (buildOccurrencePreview(values).status === 'unavailable') {
      // The engine bounds the rule on each occurrence's DUE date, so an end date
      // inside the first window produces a schedule with nothing in it. Asking
      // the preview rather than re-deriving keeps the two from disagreeing.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'No occurrence would be due on or before this date, so the schedule would never run',
        path: ['Until'],
      });
    }
  }
};

const validateWindowsDoNotOverlap = (
  values: ScheduleFormFields,
  ctx: z.RefinementCtx
): void => {
  if (!values.Repeat) {
    return;
  }

  const windows = resolveScheduleWindows(values);
  if (windows === null) {
    return;
  }

  if (findOverlap(windows)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Each occurrence would still be open when the next one starts — shorten the window or repeat less often',
      path: ['FirstDueDate'],
    });
  }
};

export const ScheduleFormSchema = scheduleFormFields.superRefine(
  (values, ctx) => {
    validateTaskType(values, ctx);
    validateDates(values, ctx);
    validateRecurrence(values, ctx);
    validateWindowsDoNotOverlap(values, ctx);
  }
);

export type ScheduleFormFieldData = z.infer<typeof ScheduleFormSchema>;

export const defaultValues: ScheduleFormFieldData = {
  Name: '',
  Description: '',
  Owners: [],
  TaskType: '',
  Targets: [],
  Assignment: 'owner',
  FirstStartDate: '',
  FirstDueDate: '',
  Repeat: false,
  Frequency: 'MONTHLY',
  Interval: '1',
  Ends: 'never',
  Count: '',
  Until: '',
};
